const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? "/api/v1").replace(/\/$/, "");

export type DependencyStatus = {
  status: "ready" | "unavailable";
  latency_ms?: number;
  detail?: string;
};

export type ReadinessResponse = {
  status: "ready" | "degraded";
  service: string;
  version: string;
  request_id: string;
  dependencies: {
    postgresql: DependencyStatus;
    redis: DependencyStatus;
  };
};

export type LoginResponse = {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
  must_change_password: boolean;
};

export type CurrentUser = {
  id: string;
  username: string;
  role: "superadmin" | "tenant" | "platform_admin" | "agent";
  tenant_id?: string;
  tenant_name?: string;
  must_change_password: boolean;
};


export type AdminOverview = {
  tenants: number;
  active_tenants: number;
  platform_users: number;
  qr_instances: number;
  connected_qr_instances: number;
};

export type Tenant = {
  id: string;
  name: string;
  slug: string;
  tenant_type?: "meta_verified" | "qr_based" | "hybrid";
  status: "active" | "suspended" | "closed";
  max_qr_instances?: number;
  max_waba_channels?: number;
  daily_message_limit?: number;
  credit_balance?: number;
  plan_name?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
};

export type ChannelAccount = {
  id: string;
  tenant_id: string;
  type: "meta_official" | "qr_whatsapp";
  name: string;
  phone_number?: string;
  identifier: string;
  waba_id?: string;
  phone_number_id?: string;
  status: "active" | "pending_verification" | "disconnected";
  quality_score: "GREEN" | "YELLOW" | "RED";
  messaging_tier: "TIER_1K" | "TIER_10K" | "TIER_100K" | "UNLIMITED";
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type QRInstance = {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  tenant_slug?: string;
  name: string;
  phone_number?: string;
  jid?: string;
  status: "provisioning" | "pairing" | "connected" | "reconnecting" | "logged_out" | "restricted" | "disabled";
  qr_code?: string;
  pairing_code?: string;
  last_connected_at?: string;
  last_heartbeat_at?: string;
  disconnect_reason?: string;
  reconnect_attempts: number;
  api_token_preview?: string;
  initial_api_token?: string;
  has_api_token?: boolean;
  api_token_created_at?: string;
  webhook_url?: string;
  webhook_secret_configured?: boolean;
  anti_ban?: {
    warmup_stage?: string;
    daily_limit?: number;
    daily_sent_count?: number;
    sent_today?: number;
    simulate_typing?: boolean;
    jitter_enabled?: boolean;
    batch_cooldown_size?: number;
    batch_cooldown_seconds?: number;
  };
  created_at: string;
  updated_at: string;
};

export type QRInstanceTokenResponse = {
  instance_id: string;
  token: string;
  preview: string;
  created_at: string;
};

export type Tag = {
  id: string;
  name: string;
  color: string;
  created_at: string;
};

export type Contact = {
  id: string;
  tenant_id: string;
  phone_e164: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  attributes: Record<string, any>;
  opt_in_status: string;
  last_seen_at?: string;
  tags: Tag[];
  created_at: string;
  updated_at: string;
};

export type LeadListSummary = {
  list_name: string;
  total_leads: number;
  new_leads: number;
  contacted_leads: number;
  qualified_leads: number;
  converted_leads: number;
  last_imported_at?: string;
};

export type Segment = {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  filter_rules: Record<string, any>;
  created_at: string;
};

export type Message = {
  id: string;
  conversation_id: string;
  direction: "inbound" | "outbound";
  sender_type: string;
  message_type: "text" | "image" | "video" | "audio" | "document" | "interactive";
  content: Record<string, any>;
  provider_message_id?: string;
  status: "queued" | "sent" | "delivered" | "read" | "failed";
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  failed_at?: string;
  error_code?: string;
  error_message?: string;
  created_at: string;
};

export type Conversation = {
  id: string;
  tenant_id: string;
  contact_id: string;
  contact?: Contact;
  channel_type: string;
  channel_instance_id?: string;
  assigned_user_id?: string;
  status: "open" | "pending" | "resolved" | "closed";
  unread_count: number;
  last_message_at: string;
  last_message_preview?: string;
  created_at: string;
};

export type CannedReply = {
  id: string;
  shortcut: string;
  title: string;
  content: string;
  category?: string;
  created_at: string;
};

export type Campaign = {
  id: string;
  tenant_id: string;
  name: string;
  channel_type: string;
  channel_instance_id?: string;
  segment_id?: string;
  status: "draft" | "scheduled" | "running" | "paused" | "completed" | "failed" | "cancelled";
  message_content: Record<string, any>;
  pacing_per_minute: number;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  scheduled_at?: string;
  completed_at?: string;
  created_at: string;
};

export type Template = {
  id: string;
  name: string;
  category: string;
  language: string;
  status: string;
  structure: Record<string, any>;
  created_at: string;
};

export type AutomationRule = {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  is_active: boolean;
  trigger_type: string;
  keywords: string[];
  action_type: string;
  action_payload: Record<string, any>;
  execution_count: number;
  created_at: string;
};

export type Wallet = {
  id: string;
  tenant_id?: string;
  account_type: string;
  currency: string;
  balance: number;
};

export type ManualReceipt = {
  id: string;
  tenant_id: string;
  recorded_by_user_id: string;
  approved_by_user_id?: string;
  payment_reference: string;
  payment_method: string;
  amount: number;
  credits_to_grant: number;
  currency: string;
  status: "pending" | "approved" | "rejected";
  notes?: string;
  approved_at?: string;
  created_at: string;
};

export type LedgerTransaction = {
  id: string;
  idempotency_key: string;
  transaction_type: string;
  description?: string;
  created_at: string;
};

export type Invoice = {
  id: string;
  tenant_id: string;
  invoice_number: string;
  total_amount: number;
  currency: string;
  status: string;
  receipt_reference?: string;
  created_at: string;
};

export type Reseller = {
  id: string;
  name: string;
  slug: string;
  contact_email: string;
  contact_phone?: string;
  status: string;
  commission_rate: number;
  custom_domain?: string;
  created_at: string;
};

export type AuditEvent = {
  id: string;
  tenant_id?: string;
  user_id?: string;
  actor_email: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  ip_address?: string;
  payload_diff: Record<string, any>;
  created_at: string;
};

export type DeliveryReport = {
  total_sent: number;
  total_delivered: number;
  total_read: number;
  total_failed: number;
  delivery_rate: number;
  read_rate: number;
  points: Array<{
    timestamp: string;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
  }>;
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function parseApiResponse<T>(response: Response): Promise<T> {
  const requestId = response.headers.get("x-request-id") ?? undefined;
  if (response.status === 204) {
    return {} as T;
  }
  let body: any;
  const rawText = await response.text();
  try {
    body = rawText ? JSON.parse(rawText) : {};
  } catch {
    body = { detail: rawText || `Request failed with status ${response.status}` };
  }
  if (!response.ok) {
    if (response.status === 401) {
      window.localStorage.removeItem("wamaster.access_token");
      window.dispatchEvent(new CustomEvent("wamaster:unauthorized"));
    }
    const detail =
      typeof body === "object" && body !== null && "detail" in body
        ? (typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail))
        : (typeof body === "string" ? body : undefined);
    throw new ApiError(detail ?? `Request failed with status ${response.status}`, response.status, requestId);
  }
  return body as T;
}


export async function getReadiness(signal?: AbortSignal): Promise<ReadinessResponse> {
  const response = await fetch(`${apiBaseUrl}/health/ready`, {
    headers: { Accept: "application/json" },
    signal,
  });
  return parseApiResponse<ReadinessResponse>(response);
}

export async function login(username: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${apiBaseUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ username, password }),
  });
  return parseApiResponse<LoginResponse>(response);
}

export async function getCurrentUser(token: string, signal?: AbortSignal): Promise<CurrentUser> {
  const response = await fetch(`${apiBaseUrl}/auth/me`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    signal,
  });
  return parseApiResponse<CurrentUser>(response);
}

export async function changePassword(token: string, currentPassword: string, nextPassword: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/auth/change-password`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: nextPassword,
    }),
  });
  await parseApiResponse<unknown>(response);
}

export type OverviewStatsResponse = {
  role: "admin" | "tenant";
  tenant_name?: string;
  tenants?: number;
  active_tenants?: number;
  platform_users?: number;
  qr_instances?: number;
  connected_qr_instances?: number;
  total_instances?: number;
  connected_instances?: number;
  total_contacts: number;
  total_campaigns?: number;
  total_messages: number;
  today_sent: number;
  wallet_credits: number;
};

export async function getOverviewStats(token: string, tenantId?: string): Promise<OverviewStatsResponse> {
  const url = tenantId ? `${apiBaseUrl}/reports/overview?tenant_id=${tenantId}` : `${apiBaseUrl}/reports/overview`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<OverviewStatsResponse>(response);
}

// Super Admin APIs
export async function getAdminOverview(token: string, signal?: AbortSignal): Promise<AdminOverview> {
  const response = await fetch(`${apiBaseUrl}/reports/overview`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    signal,
  });
  return parseApiResponse<AdminOverview>(response);
}

export async function listTenants(token: string, signal?: AbortSignal): Promise<Tenant[]> {
  const response = await fetch(`${apiBaseUrl}/admin/tenants`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    signal,
  });
  return parseApiResponse<Tenant[]>(response);
}

export async function createTenant(token: string, payload: { name: string; slug: string }): Promise<Tenant> {
  const response = await fetch(`${apiBaseUrl}/admin/tenants`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Tenant>(response);
}

export async function updateTenant(
  token: string,
  tenantId: string,
  payload: { name?: string; slug?: string; status?: string }
): Promise<Tenant> {
  const response = await fetch(`${apiBaseUrl}/admin/tenants/${tenantId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Tenant>(response);
}

export async function deleteTenant(token: string, tenantId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/admin/tenants/${tenantId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to delete tenant: ${response.status}`);
  }
}

// QR Instances APIs

export async function listQRInstances(token: string, tenantId?: string): Promise<QRInstance[]> {
  const url = tenantId ? `${apiBaseUrl}/qr-instances?tenant_id=${tenantId}` : `${apiBaseUrl}/qr-instances`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<QRInstance[]>(response);
}

export async function createQRInstance(token: string, payload: { name: string; phone_number?: string }, tenantId?: string): Promise<QRInstance> {
  const url = tenantId ? `${apiBaseUrl}/qr-instances?tenant_id=${tenantId}` : `${apiBaseUrl}/qr-instances`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<QRInstance>(response);
}

export async function updateQRInstance(token: string, instanceId: string, payload: { name?: string; settings?: Record<string, any> }): Promise<QRInstance> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<QRInstance>(response);
}

export async function refreshQRCode(token: string, instanceId: string): Promise<QRInstance> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/pair-qr`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<QRInstance>(response);
}

export async function requestPairingCode(token: string, instanceId: string, phoneNumber: string): Promise<{ pairing_code: string; phone_number: string }> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/pair-code`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ phone_number: phoneNumber }),
  });
  return parseApiResponse<{ pairing_code: string; phone_number: string }>(response);
}

export async function confirmPairing(token: string, instanceId: string, phoneNumber: string, pushName?: string): Promise<QRInstance> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/confirm-pair`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ phone_number: phoneNumber, push_name: pushName || "WhatsApp User" }),
  });
  return parseApiResponse<QRInstance>(response);
}

export async function reconnectQRInstance(token: string, instanceId: string): Promise<QRInstance> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/reconnect`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<QRInstance>(response);
}

export async function logoutQRInstance(token: string, instanceId: string): Promise<QRInstance> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/logout`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<QRInstance>(response);
}

export async function deleteQRInstance(token: string, instanceId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  await parseApiResponse(response);
}

export async function generateInstanceToken(token: string, instanceId: string): Promise<QRInstanceTokenResponse> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/token`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<QRInstanceTokenResponse>(response);
}

export async function revokeInstanceToken(token: string, instanceId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/token`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  await parseApiResponse(response);
}

export async function updateInstanceWebhook(
  token: string,
  instanceId: string,
  payload: { webhook_url?: string; webhook_secret?: string }
): Promise<QRInstance> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/webhook`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<QRInstance>(response);
}

export async function updateInstanceAntiBanSettings(
  token: string,
  instanceId: string,
  payload: {
    warmup_stage?: string;
    daily_limit?: number;
    simulate_typing?: boolean;
    jitter_enabled?: boolean;
    batch_cooldown_size?: number;
    batch_cooldown_seconds?: number;
  }
): Promise<QRInstance> {
  const response = await fetch(`${apiBaseUrl}/qr-instances/${instanceId}/antiban-settings`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<QRInstance>(response);
}

// Contacts CRM APIs
export async function listContacts(
  token: string,
  search?: string,
  tagId?: string,
  tenantId?: string,
  listName?: string,
  leadStatus?: string
): Promise<Contact[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (tagId) params.set("tag_id", tagId);
  if (tenantId) params.set("tenant_id", tenantId);
  if (listName && listName !== "all") params.set("list_name", listName);
  if (leadStatus && leadStatus !== "all") params.set("lead_status", leadStatus);
  const response = await fetch(`${apiBaseUrl}/contacts?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Contact[]>(response);
}

export async function createContact(token: string, payload: { phone_e164: string; first_name?: string; last_name?: string; email?: string; attributes?: Record<string, any>; tag_ids?: string[] }, tenantId?: string): Promise<Contact> {
  const url = tenantId ? `${apiBaseUrl}/contacts?tenant_id=${tenantId}` : `${apiBaseUrl}/contacts`;
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Contact>(response);
}

export async function updateContact(
  token: string,
  contactId: string,
  payload: { first_name?: string; last_name?: string; email?: string; opt_in_status?: string; attributes?: Record<string, any>; tag_ids?: string[] }
): Promise<Contact> {
  const response = await fetch(`${apiBaseUrl}/contacts/${contactId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Contact>(response);
}

export async function updateLeadStatus(token: string, contactId: string, leadStatus: string): Promise<Contact> {
  const response = await fetch(`${apiBaseUrl}/contacts/${contactId}/lead-status`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ lead_status: leadStatus }),
  });
  return parseApiResponse<Contact>(response);
}

export async function listLeadLists(token: string, tenantId?: string): Promise<LeadListSummary[]> {
  const url = tenantId ? `${apiBaseUrl}/contacts/lists?tenant_id=${tenantId}` : `${apiBaseUrl}/contacts/lists`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<LeadListSummary[]>(response);
}

export async function deleteContact(token: string, contactId: string): Promise<{ status: string }> {
  const response = await fetch(`${apiBaseUrl}/contacts/${contactId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<{ status: string }>(response);
}

export async function bulkImportContacts(
  token: string,
  payload: {
    contacts: Array<{ phone_e164: string; first_name?: string; last_name?: string; email?: string; attributes?: Record<string, any> }>;
    list_name?: string;
    lead_status?: string;
    tag_id?: string;
  },
  tenantId?: string
): Promise<{ status: string; imported: number; updated: number; total_processed: number; list_name?: string }> {
  const url = tenantId ? `${apiBaseUrl}/contacts/import?tenant_id=${tenantId}` : `${apiBaseUrl}/contacts/import`;
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<{ status: string; imported: number; updated: number; total_processed: number; list_name?: string }>(response);
}

export async function listTags(token: string, tenantId?: string): Promise<Tag[]> {
  const url = tenantId ? `${apiBaseUrl}/contacts/tags/list?tenant_id=${tenantId}` : `${apiBaseUrl}/contacts/tags/list`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Tag[]>(response);
}

export async function createTag(token: string, payload: { name: string; color: string }, tenantId?: string): Promise<Tag> {
  const url = tenantId ? `${apiBaseUrl}/contacts/tags?tenant_id=${tenantId}` : `${apiBaseUrl}/contacts/tags`;
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Tag>(response);
}

// Inbox APIs
export async function listConversations(token: string, status?: string, tenantId?: string, assignedTo?: string): Promise<Conversation[]> {
  const params = new URLSearchParams();
  if (status) params.set("status_filter", status);
  if (tenantId) params.set("tenant_id", tenantId);
  if (assignedTo) params.set("assigned_to", assignedTo);
  const response = await fetch(`${apiBaseUrl}/inbox/conversations?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Conversation[]>(response);
}

export async function assignConversation(
  token: string,
  conversationId: string,
  assignedUserId: string | null
): Promise<Conversation> {
  const response = await fetch(`${apiBaseUrl}/inbox/conversations/${conversationId}/assign`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ assigned_user_id: assignedUserId }),
  });
  return parseApiResponse<Conversation>(response);
}

export async function createConversation(
  token: string,
  payload: {
    phone_e164: string;
    contact_name?: string;
    channel_type?: string;
    channel_instance_id?: string;
    initial_message?: string;
  },
  tenantId?: string
): Promise<Conversation> {
  const url = tenantId ? `${apiBaseUrl}/inbox/conversations?tenant_id=${tenantId}` : `${apiBaseUrl}/inbox/conversations`;
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Conversation>(response);
}

export async function getConversationMessages(token: string, conversationId: string): Promise<Message[]> {
  const response = await fetch(`${apiBaseUrl}/inbox/conversations/${conversationId}/messages`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Message[]>(response);
}

export async function sendMessage(token: string, conversationId: string, payload: { message_type: string; content: Record<string, any>; quoted_message_id?: string }): Promise<Message> {
  const response = await fetch(`${apiBaseUrl}/inbox/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Message>(response);
}

export async function sendAttachment(
  token: string,
  conversationId: string,
  payload: { file: File; mediaType: "image" | "video" | "audio" | "document"; caption?: string; quotedMessageId?: string }
): Promise<Message> {
  const form = new FormData();
  form.append("attachment", payload.file);
  form.append("media_type", payload.mediaType);
  form.append("caption", payload.caption || "");
  if (payload.quotedMessageId) form.append("quoted_message_id", payload.quotedMessageId);
  const response = await fetch(`${apiBaseUrl}/inbox/conversations/${conversationId}/attachments`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    body: form,
  });
  return parseApiResponse<Message>(response);
}

export async function fetchMessageMedia(token: string, messageId: string): Promise<Blob> {
  const response = await fetch(`${apiBaseUrl}/inbox/messages/${messageId}/media`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new Error(detail.detail || `Media download failed (${response.status})`);
  }
  return response.blob();
}

export async function markConversationRead(token: string, conversationId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/inbox/conversations/${conversationId}/read`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  await parseApiResponse(response);
}

export async function reactToMessage(
  token: string,
  messageId: string,
  emoji: string
): Promise<{ status: string; reactions: Array<{ emoji: string; from_me?: boolean; sender?: string }> }> {
  const response = await fetch(`${apiBaseUrl}/inbox/messages/${messageId}/react`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ emoji }),
  });
  return parseApiResponse(response);
}

export async function toggleStarMessage(
  token: string,
  messageId: string
): Promise<{ status: string; is_starred: boolean }> {
  const response = await fetch(`${apiBaseUrl}/inbox/messages/${messageId}/star`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse(response);
}

export async function togglePinMessage(
  token: string,
  messageId: string
): Promise<{ status: string; is_pinned: boolean }> {
  const response = await fetch(`${apiBaseUrl}/inbox/messages/${messageId}/pin`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse(response);
}

export async function deleteMessage(
  token: string,
  messageId: string
): Promise<{ status: string; deleted_message_id: string }> {
  const response = await fetch(`${apiBaseUrl}/inbox/messages/${messageId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse(response);
}

export async function forwardMessage(
  token: string,
  messageId: string,
  targetConversationId: string
): Promise<{ status: string; forwarded_message_id: string }> {
  const response = await fetch(`${apiBaseUrl}/inbox/messages/${messageId}/forward`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ target_conversation_id: targetConversationId }),
  });
  return parseApiResponse(response);
}

export async function listCannedReplies(token: string, tenantId?: string): Promise<CannedReply[]> {
  const url = tenantId ? `${apiBaseUrl}/inbox/canned-replies?tenant_id=${tenantId}` : `${apiBaseUrl}/inbox/canned-replies`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<CannedReply[]>(response);
}

export async function createCannedReply(
  token: string,
  payload: { shortcut: string; title: string; content: string },
  tenantId?: string
): Promise<CannedReply> {
  const url = tenantId ? `${apiBaseUrl}/inbox/canned-replies?tenant_id=${tenantId}` : `${apiBaseUrl}/inbox/canned-replies`;
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<CannedReply>(response);
}

export async function updateCannedReply(
  token: string,
  replyId: string,
  payload: { shortcut?: string; title?: string; content?: string }
): Promise<CannedReply> {
  const response = await fetch(`${apiBaseUrl}/inbox/canned-replies/${replyId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<CannedReply>(response);
}

export async function deleteCannedReply(token: string, replyId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/inbox/canned-replies/${replyId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to delete canned reply: ${response.statusText}`);
  }
}

// Campaigns APIs
export async function listCampaigns(token: string, tenantId?: string): Promise<Campaign[]> {
  const url = tenantId ? `${apiBaseUrl}/campaigns?tenant_id=${tenantId}` : `${apiBaseUrl}/campaigns`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Campaign[]>(response);
}

export async function createCampaign(
  token: string,
  payload: {
    name: string;
    channel_type: string;
    channel_instance_id?: string | null;
    tag_id?: string | null;
    lead_list_name?: string | null;
    segment_id?: string | null;
    message_content: Record<string, any>;
    pacing_per_minute: number;
    scheduled_at?: string | null;
  },
  tenantId?: string
): Promise<Campaign> {
  const url = tenantId ? `${apiBaseUrl}/campaigns?tenant_id=${tenantId}` : `${apiBaseUrl}/campaigns`;
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Campaign>(response);
}

export async function startCampaign(token: string, campaignId: string): Promise<Campaign> {
  const response = await fetch(`${apiBaseUrl}/campaigns/${campaignId}/start`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Campaign>(response);
}

export async function pauseCampaign(token: string, campaignId: string): Promise<Campaign> {
  const response = await fetch(`${apiBaseUrl}/campaigns/${campaignId}/pause`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Campaign>(response);
}

// Message Templates APIs (Meta Verified & QR WhatsApp)
export type TemplateItem = {
  id: string;
  tenant_id: string;
  name: string;
  category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
  language: string;
  status: "APPROVED" | "PENDING" | "REJECTED" | "PAUSED";
  structure: {
    channel_type?: "meta_official" | "qr_whatsapp";
    header_type?: "none" | "text" | "image" | "document" | "video";
    header_content?: string;
    body_text: string;
    footer_text?: string;
    buttons?: Array<{ type: "quick_reply" | "url" | "phone"; text: string; value?: string }>;
  };
  created_at: string;
};

export async function listTemplates(token: string, tenantId?: string): Promise<TemplateItem[]> {
  const url = tenantId ? `${apiBaseUrl}/campaigns/templates/list?tenant_id=${tenantId}` : `${apiBaseUrl}/campaigns/templates/list`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<TemplateItem[]>(response);
}

export async function createTemplate(
  token: string,
  payload: {
    name: string;
    category: string;
    language: string;
    structure: Record<string, any>;
  },
  tenantId?: string
): Promise<TemplateItem> {
  const url = tenantId ? `${apiBaseUrl}/campaigns/templates?tenant_id=${tenantId}` : `${apiBaseUrl}/campaigns/templates`;
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<TemplateItem>(response);
}

export async function updateTemplate(
  token: string,
  templateId: string,
  payload: {
    name: string;
    category: string;
    language: string;
    structure: Record<string, any>;
  }
): Promise<TemplateItem> {
  const response = await fetch(`${apiBaseUrl}/campaigns/templates/${templateId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<TemplateItem>(response);
}

export async function deleteTemplate(token: string, templateId: string): Promise<{ status: string }> {
  const response = await fetch(`${apiBaseUrl}/campaigns/templates/${templateId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<{ status: string }>(response);
}

export async function submitMetaTemplate(token: string, templateId: string): Promise<TemplateItem> {
  const response = await fetch(`${apiBaseUrl}/campaigns/templates/${templateId}/submit-meta`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<TemplateItem>(response);
}

// Automations APIs
export async function listAutomations(token: string, tenantId?: string): Promise<AutomationRule[]> {
  const url = tenantId ? `${apiBaseUrl}/automations?tenant_id=${tenantId}` : `${apiBaseUrl}/automations`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<AutomationRule[]>(response);
}

export async function createAutomation(token: string, payload: { name: string; trigger_type: string; keywords: string[]; action_type: string; action_payload: Record<string, any>; is_active: boolean }, tenantId?: string): Promise<AutomationRule> {
  const url = tenantId ? `${apiBaseUrl}/automations?tenant_id=${tenantId}` : `${apiBaseUrl}/automations`;
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<AutomationRule>(response);
}

export async function updateAutomation(token: string, ruleId: string, payload: Partial<AutomationRule>): Promise<AutomationRule> {
  const response = await fetch(`${apiBaseUrl}/automations/${ruleId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<AutomationRule>(response);
}

export async function deleteAutomation(token: string, ruleId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/automations/${ruleId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  await parseApiResponse(response);
}

// Manual Billing APIs (ADR-0019)
export async function listWallets(token: string, tenantId?: string): Promise<Wallet[]> {
  const url = tenantId ? `${apiBaseUrl}/billing/wallets?tenant_id=${tenantId}` : `${apiBaseUrl}/billing/wallets`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Wallet[]>(response);
}

export async function listReceipts(token: string, tenantId?: string): Promise<ManualReceipt[]> {
  const url = tenantId ? `${apiBaseUrl}/billing/receipts?tenant_id=${tenantId}` : `${apiBaseUrl}/billing/receipts`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<ManualReceipt[]>(response);
}

export async function createManualReceipt(token: string, payload: { tenant_id: string; payment_reference: string; payment_method: string; amount: number; credits_to_grant: number; currency: string; notes?: string }): Promise<ManualReceipt> {
  const response = await fetch(`${apiBaseUrl}/billing/receipts`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<ManualReceipt>(response);
}

export async function approveManualReceipt(token: string, receiptId: string): Promise<ManualReceipt> {
  const response = await fetch(`${apiBaseUrl}/billing/receipts/${receiptId}/approve`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<ManualReceipt>(response);
}

export async function listLedger(token: string): Promise<LedgerTransaction[]> {
  const response = await fetch(`${apiBaseUrl}/billing/ledger`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<LedgerTransaction[]>(response);
}

export async function listInvoices(token: string, tenantId?: string): Promise<Invoice[]> {
  const url = tenantId ? `${apiBaseUrl}/billing/invoices?tenant_id=${tenantId}` : `${apiBaseUrl}/billing/invoices`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Invoice[]>(response);
}

// Resellers APIs
export async function listResellers(token: string): Promise<Reseller[]> {
  const response = await fetch(`${apiBaseUrl}/resellers`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<Reseller[]>(response);
}

export async function createReseller(token: string, payload: { name: string; slug: string; contact_email: string; contact_phone?: string; commission_rate: number; custom_domain?: string }): Promise<Reseller> {
  const response = await fetch(`${apiBaseUrl}/resellers`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Reseller>(response);
}

// Reports & Audit
export async function getDeliveryReport(token: string, tenantId?: string): Promise<DeliveryReport> {
  const url = tenantId ? `${apiBaseUrl}/reports/delivery?tenant_id=${tenantId}` : `${apiBaseUrl}/reports/delivery`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<DeliveryReport>(response);
}

export async function listAuditEvents(token: string, tenantId?: string): Promise<AuditEvent[]> {
  const url = tenantId ? `${apiBaseUrl}/audit/events?tenant_id=${tenantId}` : `${apiBaseUrl}/audit/events`;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<AuditEvent[]>(response);
}

// Multi-Channel APIs (Meta Verified & Pure Python QR)
export async function listChannels(token: string, channelType?: string, tenantId?: string): Promise<ChannelAccount[]> {
  const params = new URLSearchParams();
  if (channelType) params.set("channel_type", channelType);
  if (tenantId) params.set("tenant_id", tenantId);
  const response = await fetch(`${apiBaseUrl}/channels?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<ChannelAccount[]>(response);
}

export async function createChannel(
  token: string,
  payload: {
    type: "meta_official" | "qr_whatsapp";
    name: string;
    phone_number: string;
    waba_id?: string;
    phone_number_id?: string;
    access_token?: string;
  },
  tenantId?: string,
): Promise<ChannelAccount> {
  const url = tenantId ? `${apiBaseUrl}/channels?tenant_id=${tenantId}` : `${apiBaseUrl}/channels`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<ChannelAccount>(response);
}

export async function syncMetaTemplates(token: string, channelId: string): Promise<{ status: string; synced_templates: number }> {
  const response = await fetch(`${apiBaseUrl}/channels/${channelId}/sync-templates`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<{ status: string; synced_templates: number }>(response);
}

export interface MetaConnectionTestResult {
  status: string;
  verified_name?: string | null;
  display_phone_number?: string | null;
  quality_rating?: string | null;
  messaging_limit_tier?: string | null;
  code_verification_status?: string | null;
  error?: string | null;
}

export async function testMetaConnection(
  token: string,
  payload: { phone_number_id: string; access_token: string }
): Promise<MetaConnectionTestResult> {
  const response = await fetch(`${apiBaseUrl}/channels/test-meta-connection`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<MetaConnectionTestResult>(response);
}

export async function deleteChannel(token: string, channelId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/channels/${channelId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  await parseApiResponse(response);
}

// -------------------------------------------------------------
// Extended Types for eNotify (QR Suite) & NotBot (Meta WABA Suite)
// -------------------------------------------------------------

export type CustomField = {
  id: string;
  title: string;
  field_id: string;
  type: "text" | "number" | "date" | "dropdown";
  description?: string;
  created_at: string;
};

export type TeamMember = {
  id: string;
  name: string;
  email: string;
  role: "Super User" | "Agent" | "Admin";
  is_verified: boolean;
  last_login_at: string;
  added_on: string;
  assigned_channels?: string[];
};

export type MessageQueueItem = {
  id: string;
  queue_id: string;
  instance_name: string;
  receiver: string;
  priority: "high" | "normal" | "low";
  scheduled_at: string;
  attempts: number;
  status: "queued" | "processing" | "failed" | "sent";
  payload_preview: string;
};

export type DeliveryReportItem = {
  id: string;
  message_id: string;
  user_name: string;
  instance_name: string;
  receiver: string;
  type: string;
  content: string;
  status: "sent" | "delivered" | "read" | "failed";
  submitted_at: string;
  sent_at: string;
  delivered_at?: string;
  read_at?: string;
  error_code?: string;
};

export type DailyUsageRecord = {
  date: string;
  instance_name: string;
  user_name: string;
  sent_count: number;
  delivered_count: number;
  failed_count: number;
  in_queue: number;
};

export type ConnectionReportItem = {
  instance_name: string;
  phone_number: string;
  socket_status: "connected" | "reconnecting" | "disconnected";
  ping_latency_ms: number;
  last_connected_at: string;
  disconnect_reason?: string;
  uptime_percentage: number;
  days_remaining: number;
};

export type UserManagementItem = {
  id: string;
  name: string;
  phone: string;
  tenant_name: string;
  credits: number | "NA";
  role: "User" | "Super User" | "Tenant Admin";
  status: "Active" | "Suspended";
  created_at: string;
};

// Fallback / Query API handlers
export async function listCustomFields(): Promise<CustomField[]> {
  return [
    { id: "cf-1", title: "Customer Account Number", field_id: "account_no", type: "text", description: "Internal ERP Account ID", created_at: "2026-08-15" },
    { id: "cf-2", title: "Lead Priority Score", field_id: "lead_score", type: "number", description: "CRM qualification score 1-100", created_at: "2026-08-18" },
    { id: "cf-3", title: "Subscription Renewal Date", field_id: "renewal_date", type: "date", description: "SaaS annual renewal", created_at: "2026-08-20" },
    { id: "cf-4", title: "Industry Vertical", field_id: "industry", type: "dropdown", description: "E-Commerce / Real Estate / Healthcare", created_at: "2026-08-22" },
  ];
}

export async function listTeamMembers(): Promise<TeamMember[]> {
  return [
    { id: "tm-1", name: "Anjani Kumar", email: "anjani@enterprise.com", role: "Super User", is_verified: true, last_login_at: "2026-09-02 18:40", added_on: "2026-01-10", assigned_channels: ["WABA Official", "QR Node 1"] },
    { id: "tm-2", name: "Vishu Sharma", email: "vishu@enterprise.com", role: "Agent", is_verified: true, last_login_at: "2026-09-02 17:15", added_on: "2026-03-14", assigned_channels: ["WABA Official"] },
    { id: "tm-3", name: "King Khan", email: "kkhan@enterprise.com", role: "Agent", is_verified: true, last_login_at: "2026-09-01 12:00", added_on: "2026-05-20", assigned_channels: ["QR Node 2"] },
    { id: "tm-4", name: "Leena Bhatia", email: "leena@enterprise.com", role: "Admin", is_verified: true, last_login_at: "2026-09-02 19:10", added_on: "2026-06-01", assigned_channels: ["WABA Official", "QR Node 1", "QR Node 2"] },
  ];
}

export async function listMessageQueue(): Promise<MessageQueueItem[]> {
  return [
    { id: "q-101", queue_id: "Q-99021", instance_name: "VIP Support QR", receiver: "+919833332133", priority: "high", scheduled_at: "Immediate", attempts: 0, status: "processing", payload_preview: "Your VIP verification code is 889211" },
    { id: "q-102", queue_id: "Q-99022", instance_name: "Marketing Node 1", receiver: "+919769311484", priority: "normal", scheduled_at: "In 2 mins", attempts: 0, status: "queued", payload_preview: "Exclusive weekend sale: 30% off with code VIP30" },
    { id: "q-103", queue_id: "Q-99023", instance_name: "Marketing Node 1", receiver: "+918591086380", priority: "normal", scheduled_at: "In 4 mins", attempts: 0, status: "queued", payload_preview: "Exclusive weekend sale: 30% off with code VIP30" },
    { id: "q-104", queue_id: "Q-99024", instance_name: "Transactional QR", receiver: "+919167228608", priority: "high", scheduled_at: "Immediate", attempts: 1, status: "processing", payload_preview: "Order #8832 dispatched via BlueDart. Track: BD992" },
    { id: "q-105", queue_id: "Q-99025", instance_name: "VIP Support QR", receiver: "+917765936209", priority: "low", scheduled_at: "In 15 mins", attempts: 0, status: "queued", payload_preview: "Monthly service maintenance notice scheduled for Sunday" },
  ];
}

export async function listDeliveryReportsDetailed(): Promise<DeliveryReportItem[]> {
  return [
    { id: "rep-1", message_id: "wamid.HBgMOTE5ODMzMzgx", user_name: "DELUXE (918454818800)", instance_name: "VIP Support QR", receiver: "+919833332133", type: "text", content: "Your VIP pass #9921 is now active", status: "read", submitted_at: "2026-09-02 18:30:00", sent_at: "2026-09-02 18:30:02", delivered_at: "2026-09-02 18:30:05", read_at: "2026-09-02 18:30:42" },
    { id: "rep-2", message_id: "wamid.HBgMOTE5NzY5MzEx", user_name: "sairam (919326577728)", instance_name: "Marketing Node 1", receiver: "+919769311484", type: "template", content: "Q3 Announcement & Product Catalog", status: "delivered", submitted_at: "2026-09-02 18:28:10", sent_at: "2026-09-02 18:28:12", delivered_at: "2026-09-02 18:28:18" },
    { id: "rep-3", message_id: "wamid.HBgMOTE4NTkxMDg2", user_name: "aristo (919326684839)", instance_name: "Transactional QR", receiver: "+918591086380", type: "media_pdf", content: "Invoice_INV-2026-09.pdf", status: "read", submitted_at: "2026-09-02 18:25:00", sent_at: "2026-09-02 18:25:03", delivered_at: "2026-09-02 18:25:08", read_at: "2026-09-02 18:26:10" },
    { id: "rep-4", message_id: "wamid.HBgMOTE5MTY3MjI4", user_name: "DELUXE (918454818800)", instance_name: "Marketing Node 1", receiver: "+919167228608", type: "text", content: "Reminder: Webinar starts in 1 hour", status: "sent", submitted_at: "2026-09-02 18:20:00", sent_at: "2026-09-02 18:20:04" },
    { id: "rep-5", message_id: "wamid.HBgMOTE3NzY1OTM2", user_name: "twix new (918097873440)", instance_name: "VIP Support QR", receiver: "+917765936209", type: "text", content: "Password reset OTP: 449210", status: "failed", submitted_at: "2026-09-02 18:15:00", sent_at: "2026-09-02 18:15:05", error_code: "404_PHONE_NOT_ON_WHATSAPP" },
  ];
}

export async function listUsageAndConnections(token?: string, tenantId?: string): Promise<{
  usage: DailyUsageRecord[];
  connections: ConnectionReportItem[];
}> {
  if (token) {
    try {
      const url = new URL(`${apiBaseUrl}/reports/usage-and-connections`);
      if (tenantId && tenantId !== "all") {
        url.searchParams.set("tenant_id", tenantId);
      }
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      return await parseApiResponse<{ usage: DailyUsageRecord[]; connections: ConnectionReportItem[] }>(response);
    } catch {
      // Fallback
    }
  }
  return {
    usage: [],
    connections: [],
  };
}

export type QueueReportItem = {
  id: string;
  tenant_name: string;
  tenant_phone: string;
  user_name: string;
  user_phone: string;
  instance_name: string;
  instance_key: string;
  connection_detail: string;
  connection_state: string;
  queue_date: string;
  queue_count: number;
  destination?: string;
  content?: string;
};

export async function listQueueReports(token?: string, tenantId?: string): Promise<QueueReportItem[]> {
  if (token) {
    try {
      const url = new URL(`${apiBaseUrl}/reports/queue`);
      if (tenantId && tenantId !== "all") {
        url.searchParams.set("tenant_id", tenantId);
      }
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      return await parseApiResponse<QueueReportItem[]>(response);
    } catch {
      // Fallback
    }
  }
  return [];
}

export async function rechargeCredits(
  token: string,
  payload: { tenant_id: string; amount: number; credits: number; notes: string; idempotency_key: string }
): Promise<Wallet> {
  const response = await fetch(`${apiBaseUrl}/billing/recharge`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<Wallet>(response);
}

export type PlatformUserItem = {
  id: string;
  username: string;
  role: "admin" | "tenant" | "agent";
  tenant_id?: string;
  tenant_name?: string;
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
};

export async function listPlatformUsers(token: string): Promise<PlatformUserItem[]> {
  const response = await fetch(`${apiBaseUrl}/admin/users`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<PlatformUserItem[]>(response);
}

export async function createPlatformUser(
  token: string,
  payload: { username: string; password: string; role: "admin" | "tenant" | "agent"; tenant_id?: string; company_name?: string; is_active?: boolean }
): Promise<PlatformUserItem> {
  const response = await fetch(`${apiBaseUrl}/admin/users`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<PlatformUserItem>(response);
}

export async function updatePlatformUser(
  token: string,
  userId: string,
  payload: { username?: string; password?: string; role?: "admin" | "tenant" | "agent"; tenant_id?: string; company_name?: string; is_active?: boolean }
): Promise<PlatformUserItem> {
  const response = await fetch(`${apiBaseUrl}/admin/users/${userId}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<PlatformUserItem>(response);
}

export async function deletePlatformUser(token: string, userId: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/admin/users/${userId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Failed to delete user: ${response.status}`);
  }
}


export async function listUserManagement(token?: string): Promise<PlatformUserItem[]> {
  if (token) {
    try {
      return await listPlatformUsers(token);
    } catch {
      // fallback
    }
  }
  return [
    { id: "usr-admin", username: "admin", role: "admin", tenant_name: "Global System", is_active: true, created_at: "2026-01-01" },
    { id: "usr-tenant", username: "tenant_demo", role: "tenant", tenant_name: "Interlink Consultant", is_active: true, created_at: "2026-01-15" },
    { id: "usr-deluxe", username: "deluxe_ops", role: "tenant", tenant_name: "Deluxe Enterprises", is_active: true, created_at: "2026-02-01" },
  ];
}

export type GlobalDeliveryMetric = {
  tenant_id: string;
  tenant_name: string;
  tenant_type: "meta_verified" | "qr_based" | "hybrid";
  total_sent: number;
  delivered_rate: number;
  read_rate: number;
  failed_count: number;
  active_instances: number;
  today_spend_credits: number;
};

export async function listGlobalDeliveryMetrics(): Promise<GlobalDeliveryMetric[]> {
  return [
    { tenant_id: "t-1", tenant_name: "Interlink Global Enterprises", tenant_type: "hybrid", total_sent: 45200, delivered_rate: 98.4, read_rate: 76.2, failed_count: 12, active_instances: 8, today_spend_credits: 1450 },
    { tenant_id: "t-2", tenant_name: "Retail Hub Multi-Store", tenant_type: "qr_based", total_sent: 18900, delivered_rate: 97.1, read_rate: 68.5, failed_count: 24, active_instances: 5, today_spend_credits: 620 },
    { tenant_id: "t-3", tenant_name: "FinTech Alerts Direct", tenant_type: "meta_verified", total_sent: 89400, delivered_rate: 99.8, read_rate: 89.1, failed_count: 2, active_instances: 2, today_spend_credits: 3200 },
    { tenant_id: "t-4", tenant_name: "Healthcare Notification Network", tenant_type: "meta_verified", total_sent: 24500, delivered_rate: 99.2, read_rate: 82.4, failed_count: 5, active_instances: 1, today_spend_credits: 890 },
    { tenant_id: "t-5", tenant_name: "Logistics Fleet Express", tenant_type: "qr_based", total_sent: 32100, delivered_rate: 96.8, read_rate: 71.0, failed_count: 38, active_instances: 6, today_spend_credits: 1100 },
  ];
}

export type PricingMatrixItem = {
  id: string;
  channel_type: string;
  category: string;
  currency: string;
  base_cost: number;
  client_rate: number;
  markup_percent: number;
  is_active: boolean;
};

export async function getPricingMatrix(token: string): Promise<PricingMatrixItem[]> {
  const response = await fetch(`${apiBaseUrl}/billing/pricing-matrix`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<PricingMatrixItem[]>(response);
}

export async function updatePricingMatrix(
  token: string,
  items: { id: string; base_cost: number; client_rate: number; markup_percent: number; is_active: boolean }[]
): Promise<PricingMatrixItem[]> {
  const response = await fetch(`${apiBaseUrl}/billing/pricing-matrix`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ items }),
  });
  return parseApiResponse<PricingMatrixItem[]>(response);
}

export type SubscriptionPlanItem = {
  id: string;
  name: string;
  slug: string;
  monthly_price: number;
  credit_allowance: number;
  max_qr_instances: number;
  max_waba_numbers: number;
  included_qr_messages: number;
  included_meta_credits: number;
  over_quota_qr_rate: number;
  features: Record<string, any>;
  is_active: boolean;
  created_at: string;
};

export async function listSubscriptionPlans(token: string): Promise<SubscriptionPlanItem[]> {
  const response = await fetch(`${apiBaseUrl}/billing/plans`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<SubscriptionPlanItem[]>(response);
}

export async function createSubscriptionPlan(
  token: string,
  payload: {
    name: string;
    slug: string;
    monthly_price: number;
    credit_allowance?: number;
    max_qr_instances?: number;
    max_waba_numbers?: number;
    included_qr_messages?: number;
    included_meta_credits?: number;
    over_quota_qr_rate?: number;
    features?: Record<string, any>;
  }
): Promise<SubscriptionPlanItem> {
  const response = await fetch(`${apiBaseUrl}/billing/plans`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<SubscriptionPlanItem>(response);
}

export async function assignTenantPlan(
  token: string,
  tenantId: string,
  payload: {
    plan_id?: string;
    max_qr_instances?: number;
    max_waba_numbers?: number;
    custom_qr_rate?: number;
    custom_meta_markup_percent?: number;
    overdraft_limit?: number;
  }
): Promise<any> {
  const response = await fetch(`${apiBaseUrl}/billing/tenants/${tenantId}/assign-plan`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  return parseApiResponse<any>(response);
}

export type TenantBillingSummary = {
  tenant_id: string;
  tenant_name: string;
  wallet_balance: number;
  currency: string;
  overdraft_limit: number;
  total_available: number;
  plan?: {
    id: string;
    name: string;
    monthly_price: number;
    included_qr_messages: number;
  };
  qr_instances: {
    used: number;
    max: number;
  };
  rates: {
    qr_standard: number;
    meta_authentication: number;
    meta_utility: number;
    meta_marketing: number;
  };
};

export async function getTenantBillingSummary(token: string, tenantId: string): Promise<TenantBillingSummary> {
  const response = await fetch(`${apiBaseUrl}/billing/tenants/${tenantId}/summary`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  return parseApiResponse<TenantBillingSummary>(response);
}
